import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RecordedError } from 'src/app/global/components/dialogs/report-problem-dialog/report-problem.model';

const MAX_ERRORS = 10;
const MAX_MESSAGE_LENGTH = 200;
const EMAIL_PATTERN = /[^\s/@?&=]+@[^\s/@?&=]+/g;
const ENCODED_AT_PATTERN = /%40/gi;
// Backend errors answer translation keys such as `organisation.errors.notFound`: only those are kept.
// Short segments and no base64url JSON prefix (`eyJ`), so a JWT cannot pass for a key.
const MESSAGE_KEY_PATTERN = /^[a-z][a-zA-Z0-9-]{0,39}(\.[a-zA-Z0-9_-]{1,40}){1,5}$/;
const TOKEN_SEGMENT_PATTERN = /(^|\.)eyJ/;

function isMessageKey(value: unknown): value is string {
    return typeof value === 'string' && MESSAGE_KEY_PATTERN.test(value) && !TOKEN_SEGMENT_PATTERN.test(value);
}

function maskEmails(value: string): string {
    return value.replace(ENCODED_AT_PATTERN, '@').replace(EMAIL_PATTERN, '***');
}

function toPathname(url: string): string {
    try {
        return new URL(url, window.location.origin).pathname;
    } catch {
        const [path] = url.split(/[?#]/);
        return path;
    }
}

/** Path only: query strings and fragments can carry search terms or personal data. */
export function toSafePath(url: string): string {
    return maskEmails(toPathname(url));
}

export function toSafeMessage(message: string): string {
    return maskEmails(message || '').slice(0, MAX_MESSAGE_LENGTH);
}

/**
 * Keeps the last few HTTP and JS errors in memory so a problem report can carry them.
 * Never stores headers or tokens; from a response body, only a backend translation key is kept.
 */
@Injectable({
    providedIn: 'root',
})
export class DiagnosticsService {
    private errors: RecordedError[] = [];

    recordHttpError(method: string, url: string, error: HttpErrorResponse): void {
        // HttpErrorResponse.message embeds the full URL, query string included: use the backend error key instead.
        const backendMessage = isMessageKey(error.error?.message) ? error.error.message : '';
        this.push({
            kind: 'http',
            method,
            path: toSafePath(url),
            status: error.status,
            message: toSafeMessage(backendMessage || error.statusText),
        });
    }

    recordJsError(error: unknown): void {
        const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
        this.push({ kind: 'js', message: toSafeMessage(message) });
    }

    recent(): RecordedError[] {
        return [...this.errors];
    }

    private push(error: Omit<RecordedError, 'at'>): void {
        this.errors = [...this.errors, { ...error, at: new Date().toISOString() }].slice(-MAX_ERRORS);
    }
}
